import { Link } from "gatsby"
import React, { useContext } from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import { StaticImage } from "gatsby-plugin-image"

import styled from "styled-components"

import WebriQForm from "@webriq/gatsby-webriq-form"

const Banner = () => {
  return (
    <>
      <div className="hero-new-study">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mx-auto">
              <div className="title-banner">
                <h1 className="text-center">
                  How a Fortune 10 Tech Firm Rebuilt Intake to Align 2,500+
                  Projects Across 19 Partner Teams
                </h1>
                <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-start text-center text-sm-left text-white">
                  <p className="pr-sm-1 mb-1 mb-sm-0">
                    <strong>Summary:</strong>
                  </p>
                  <p>
                    A rigid intake tool left the client unable to adapt across
                    2,500+ construction projects—causing duplication, delays,
                    and misaligned approvals across 19 partner teams. Quandary
                    replaced it with a low-code system that enables real-time
                    updates and project-level visibility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  padding: 24px 0;
`

const LogoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 12px;
`

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 48px;
  object-fit: contain;
`

const Custom = ({ location }) => {
  return (
    <>
      <Layout banner={<Banner />} location={location}>
        <Seo
          title="Intake Overhaul for Global Data Center Construction Projects"
          description="We helped a tech giant cut intake updates from 12 months to days, regaining control over 2,500+ global construction projects with a low-code rebuild."
        />
        <div className="py-4 py-md-5 case-studies-inner new-case-studies-inner bg-white">
          <div className="container">
            <div className="row">
              <div className="col-md-4 col-lg-4">
                <div
                  className="sidebar-tab relative results"
                  style={{ position: "sticky", top: "110px" }}
                >
                  <div className="relative results bg-case-study p-4">
                    <h3 className="mt-0 ">Results Snapshot</h3>
                    <ul className="ml-0">
                      <li>Updates reduced from 12 months to days</li>
                      <li>2,500+ projects tracked in real time</li>
                      <li>Faster approvals across 19 partner teams</li>
                      <li>Clearer project scope and ownership</li>
                      <li>Intake aligned to strategic priorities</li>
                    </ul>
                  </div>
                  <a
                    className="btn btn-primary btn-arrow mr-2 mt-4 text-center relative w-full"
                    download
                    target="_blank"
                    href="https://cdn.sanity.io/files/55lcecww/production/ab4f8eb435942682e40a5dbcbb64480840831e17.pdf"
                  >
                    Download This Story
                  </a>
                </div>
              </div>
              <div className="col-md-8 col-lg-8">
                <section className="relative">
                  <div className="relative px-0">
                    <div className="row mx-0">
                      <div className="col-md-11 mx-auto p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Challenge</h2>
                          <p>
                            The client’s internal intake tool, used to manage
                            thousands of global data center construction
                            projects, had become too rigid to keep up with
                            shifting business priorities.
                          </p>
                          <p>
                            Updates could take 9–12 months, forcing teams to
                            move forward with outdated scopes, incomplete
                            information, and stale strategic inputs.
                          </p>
                          <p>
                            Project managers wasted time scoping, budgeting, and
                            scheduling work that no longer aligned with real
                            needs. Some projects launched only to be canceled
                            later (after budget and time had already been
                            spent).
                          </p>
                          <p>
                            With 2,500+ active projects and over 1,000 users
                            across 19 partner teams, the lack of grouping and
                            consistent prioritization created constant friction.
                            Varying standards and timelines led to conflicting
                            submissions and missed coordination.
                          </p>
                          <p>
                            The tool only supported a single level of
                            granularity. Projects with multiple phases (like
                            scoping, funding, and construction) had to be
                            submitted separately, creating duplication,
                            confusion, and rework at scale.
                          </p>
                          <p>
                            Off-the-shelf solutions lacked the flexibility the
                            team needed. And sticking with the legacy tool meant
                            staying locked into a manual, slow-moving workflow
                            with no way to scale or provide leadership the
                            visibility they needed to make confident portfolio
                            decisions.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Our Solution</h2>
                          <p>
                            The Client needed a faster way to adapt intake
                            processes across thousands of complex, high-value
                            construction projects.
                          </p>
                          <p>
                            We built a low-code application in Quickbase that
                            replaced their rigid legacy tool with a system that
                            could be updated in days—not months.
                          </p>
                          <p>
                            The new system collects and organizes intake data
                            from 1,000+ users across 2,500+ projects. Teams can
                            now group related phases (scoping, funding, design,
                            construction) under a single intake record while
                            tracking each through separate workflows, reducing
                            duplication, clarifying ownership, and enabling
                            strategic approvals.
                          </p>
                          <p>
                            We also introduced centralized grouping across
                            campuses, allowing multi-site programs to be managed
                            as unified efforts. That gave portfolio managers
                            clearer visibility into total workload, overlapping
                            timelines, and where priorities conflicted.
                          </p>
                          <p>
                            Built with{" "}
                            <Link to="/partners/quickbase/">Quickbase,</Link>{" "}
                            the tool lets the business adapt intake logic
                            without IT support, responding to shifting partner
                            needs, changing budgets, and evolving planning
                            cycles in real time.
                          </p>
                          <p>
                            <strong>Key Features:</strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              <strong>Structured Intake:</strong> Turned vague
                              requests into scoped projects with clear cost,
                              schedule, and ownership
                            </li>
                            <li>
                              <strong>Role-Based Approvals:</strong> Assigned
                              approvers, backups, and exceptions to speed up
                              decisions and ensure accountability
                            </li>
                            <li>
                              <strong>Interactive Scheduling:</strong> Enabled
                              Gantt-based timelines with real-time visibility
                              into conflicts and dependencies
                            </li>
                            <li>
                              <strong>Prioritization by Requirements:</strong>{" "}
                              Captured scope, budget, and value to guide smarter
                              project decisions
                            </li>
                            <li>
                              <strong>Partner Scope Control:</strong> Let
                              partner teams decline misaligned requests and stay
                              focused on strategic work
                            </li>
                            <li>
                              <strong>Action Tracking:</strong> Logged every
                              intake step from request to approval, increasing
                              transparency across 19 teams
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>Our Process</h2>
                          <p>
                            The client had already diagnosed the core issues and
                            outlined a clear direction forward. They brought us
                            in to build the Quickbase solution and pressure-test
                            the process design.
                          </p>
                          <p>
                            We began with{" "}
                            <Link to="/our-approach">
                              a focused discovery phase
                            </Link>{" "}
                            to evaluate their existing workflows and data
                            structure. From there, we worked side-by-side with
                            their team to co-design a new intake process aligned
                            with real-world delivery needs.
                          </p>
                          <p>
                            <strong>Key Steps:</strong>
                          </p>
                          <ol className="decimal">
                            <li>
                              <strong>
                                Designed a partner-first approval model
                              </strong>{" "}
                              that captured business input before teams scoped
                              timelines or budgets
                            </li>
                            <li>
                              <strong>
                                Created a unified workflow for global programs
                              </strong>{" "}
                              so multi-campus projects could move as one, not
                              dozens of fragmented submissions
                            </li>
                            <li>
                              <strong>
                                Built and refined the application in Quickbase
                              </strong>{" "}
                              with weekly checkpoints for feedback and rapid
                              iteration
                            </li>
                            <li>
                              <strong>Trained teams across all regions</strong>{" "}
                              to ensure consistent usage and adoption across 19
                              partner groups
                            </li>
                          </ol>
                          <p>
                            Each iteration was tested directly with users. By
                            launch, teams were already working in the structure
                            and ready to scale it.
                          </p>
                          <p>
                            We partnered closely with the Quickbase ecosystem
                            throughout the build, ensuring the final solution
                            leveraged best practices and aligned with the
                            platform’s long-term roadmap.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-gradient">
                        <div className="client-content">
                          <h2 className="text-white">The Roadblocks</h2>
                          <p className="text-white">
                            The client’s teams used the waterfall approach for
                            application development. Shifting to an agile
                            process required new rhythms of collaboration,
                            faster feedback cycles, and greater involvement from
                            stakeholders across the organization.
                          </p>
                          <p className="text-white">
                            The timeline was tight. They needed to replace their
                            legacy intake tool quickly to avoid further
                            misalignment on project approvals. We delivered a
                            working solution within weeks, not months.
                          </p>
                          <p className="text-white">
                            The bigger challenge was scale. The Intake Team
                            served 19 partner organizations, each with its own
                            workflows, priorities, and expectations. To drive
                            adoption, we worked with the organization to gather
                            feedback daily and build it directly into the
                            evolving application.
                          </p>
                          <p className="text-white">
                            The rollout also introduced structural change. Teams
                            had to rethink how they scoped and prioritized work
                            to stay within budget. The new approval process
                            shifted decisions earlier in the cycle, which
                            created friction. We coached team leads through the
                            transition and reinforced the value of the new
                            model.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Toolstack</h2>
                          <LogoGrid>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/78673169bfad856d32e060c5bf029890db184c60-261x101.png"
                                alt="Quickbase"
                              />
                            </LogoCard>
                          </LogoGrid>
                          <p>
                            The application was built in Quickbase, selected for
                            its flexibility, scalability, and support for
                            complex approval logic across thousands of projects.
                          </p>
                          <p>
                            Quickbase’s low-code environment allowed us to build
                            modular workflows, adapt quickly to team feedback,
                            and deliver working functionality in days{" "}
                            <span className="italic">(not months)</span>.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-blue">
                        <div className="client-content">
                          <h2 className="text-white">The Results</h2>
                          <p className="text-white">
                            The new system cut months from the intake timeline.
                            Approvals happen faster and with clearer criteria,
                            reducing rework and keeping teams focused on aligned
                            priorities. The business now has a scalable solution
                            that it can update without developer support.
                          </p>
                          <p className="text-white">
                            They now have clear visibility into project scope,
                            ownership, and approval status for 2,500
                            construction projects. Teams can track progress,
                            request budgets, and align projects to strategic
                            goals with less duplication and delay.
                          </p>
                          <p className="text-white">
                            More importantly, intake decisions now happen at the
                            right time, with the right context. Instead of
                            chasing approvals after work is already scoped, the
                            client’s teams can evaluate whether a project fits
                            business priorities before investing time and
                            budget.
                          </p>
                          <p className="text-white">
                            This shift helped their team regain control of their
                            global project pipeline and established a more
                            agile, accountable planning process across 19
                            partner teams.
                          </p>
                          <p>
                            <strong className="text-white">Key Results:</strong>
                          </p>
                          <ol className="decimal text-white">
                            <li className="text-white">
                              Cut system update cycles from 9–12 months to days
                              or minutes
                            </li>
                            <li className="text-white">
                              Enabled project-level tracking across 2,500+
                              active initiatives
                            </li>
                            <li className="text-white">
                              Reduced rework by scoping only strategically
                              aligned projects
                            </li>
                            <li className="text-white">
                              Improved portfolio visibility for global
                              leadership and partners
                            </li>
                            <li className="text-white">
                              Increased accountability with phase-level tracking
                              and audit trails
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>What’s Next?</h2>
                          <p>
                            With the new intake system in place, the client is
                            actively evolving how they plan, approve, and manage
                            global construction work. They continue to refine
                            prioritization criteria, streamline partner
                            workflows, and expand usage across new teams.
                          </p>
                          <p>
                            <Link to="/faq">We remain engaged</Link> to support
                            these improvements, providing technical updates,
                            process recommendations, and ongoing advisory
                            support as their team scales intake strategy across
                            their data center portfolio.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Client Overview:</h2>
                          <p>
                            A U.S.-based multinational technology company
                            operating in cloud infrastructure, digital services,
                            and consumer electronics.
                          </p>
                          <p>
                            The client’s project planning team manages
                            large-scale construction programs for global data
                            centers. Our work focused on their intake and
                            portfolio management overhaul.
                          </p>
                          <div className="row align-items-center">
                            <div className="col-md-6 pr-md-4">
                              <p>
                                <strong>Industry:</strong> Technology
                                <span className="relative d-block">
                                  <strong>Location:</strong> Global
                                </span>
                                <span className="relative d-block">
                                  <strong>Size:</strong> 100,000+ employees
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto px-0 pt-4 content-wrapper">
                        <div className="client-content">
                          <div id="speak-with-specialist">
                            <div className="form-wrap-contact">
                              <div className="form-wrapper">
                                <div className="intro mb-2">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <h2 className="mt-0 font-weight-medium">
                                        Speak with Our Innovation Team
                                      </h2>
                                      <p>
                                        Use the form below to drop us an e-mail.
                                        We'll be in touch shortly.
                                      </p>
                                      <small className="text-red">
                                        Fields marked with an asterisk (*) are
                                        required.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <WebriQForm
                                  id="contact-form"
                                  name="Contact Form"
                                  className="contactForm"
                                  data-form-id="4d46fe65-4d66-46b4-946c-e5826349ed3c"
                                  data-thankyou-url="/thank-you"
                                >
                                  <div className="row">
                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                      {/* <p class="required">Full Name</p> */}

                                      <input
                                        type="text"
                                        name="Full Name"
                                        size={40}
                                        className="form-control required"
                                        placeholder="Full Name *"
                                        aria-label="Full Name"
                                        required
                                        id="requiredField"
                                      />
                                    </div>
                                    <div className="col-lg-6 col-md-12 col-sm-12 mt-4 mt-lg-0">
                                      {/* <p class="required">Phone</p> */}
                                      <input
                                        type="text"
                                        name="Phone"
                                        size={40}
                                        // minLength={14}
                                        className="form-control required"
                                        id="phone"
                                        placeholder="Phone Number *"
                                        aria-label="Phone"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Email</p> */}
                                      <input
                                        type="email"
                                        name="Email"
                                        size={40}
                                        className="form-control"
                                        aria-label="Email"
                                        placeholder="Email *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Message</p> */}
                                      <textarea
                                        name="Message"
                                        rows={5}
                                        cols={0}
                                        className="form-control"
                                        aria-label="Message"
                                        placeholder="Message *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">How did you find out about us?</p> */}
                                      <div>
                                        <textarea
                                          type="text"
                                          name="How did you find out about us?"
                                          size={40}
                                          rows={5}
                                          className="form-control"
                                          aria-label="How did you find out about us?"
                                          placeholder="How did you find out about us? *"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="form-group col-12 d-flex justify-content-start captcha-wrap">
                                      <div className="webriq-recaptcha" />
                                    </div>
                                    <div className="form-group col-12 text-left">
                                      <div className="input-filled">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-arrow"
                                        >
                                          SUBMIT
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </WebriQForm>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default Custom
